import { useMemo } from "react";
import type { Topic } from "~/lqs";
import type { AsyncOperationSnapshot } from "~/types";
import { bigintMin } from "~/utils/math";
import {
  useLoadedPlayerTopics,
  useSkipToFirstTimestamp,
  useUpdatePanelBuffering,
} from "../../hooks";
import type { PairedTopics, ThreeDPanel } from "../../panels";
import {
  usePairedTopics,
  usePanelLayoutContext,
  VisualizationType,
} from "../../panels";
import { useLoadedPlaybackSource, usePlaybackSettings } from "../../playback";
import type {
  LimitedRecordsRequest,
  PlayerRecord,
  RecordsResponse,
  RecordStore,
  StoreSnapshot,
} from "../../record-store";
import {
  calculateMinimumCacheCount,
  getFrequencyForTimestep,
  mergeSubscriptions,
  NoRecordsError,
  RecordStoreError,
  useStoreSnapshot,
} from "../../record-store";
import type { TimestepValue } from "../../types";
import { useVisualizationStoreParams } from "../context";
import type { TransformNode } from "./transforms";
import { buildTransformTree } from "./transforms";

// Assuming we're willing to fill up 10GB of memory with point clouds and
// each decoded point cloud is ~2MB. The 2MB assumption may be high but that's
// safer than estimating too low and the user actually running out of memory.
const MAX_IN_MEMORY_POINT_CLOUDS = 5_000;

interface StoreRequest {
  pairedTopics: PairedTopics<ThreeDPanel["topics"]>;
  tfStaticTopic: Topic | null;
  showRotationControls: boolean;
  timestep: TimestepValue;
  count: number;
  timestamp: bigint;
}

export interface PointCloudObject {
  id: string;
  type: "point-cloud";
  data: PlayerRecord<"threeD">["data"];
  size: number;
}

export type ThreeDObject = PointCloudObject;

export interface ThreeDRecords {
  showRotationControls: boolean;
  objects: ReadonlyArray<ThreeDObject>;
  transforms: ReadonlyArray<TransformNode> | null;
  firstTimestamp: bigint | null;
}

export type ThreeDStoreSnapshot = StoreSnapshot<StoreRequest, ThreeDRecords>;

function createRecordsRequests(
  pairedTopics: PairedTopics<ThreeDPanel["topics"]>,
  timestep: TimestepValue,
  count: number,
  timestamp: bigint,
): ReadonlyArray<LimitedRecordsRequest<"threeD">> {
  const frequency = getFrequencyForTimestep(timestep);

  return pairedTopics.map(({ topic }) => ({
    type: "limited",
    recordType: "threeD",
    topicId: topic.id,
    frequency,
    timestamp,
    count,
  }));
}

function subscribeToStore(
  pairedTopics: PairedTopics<ThreeDPanel["topics"]>,
  tfStaticTopic: Topic | null,
  timestep: TimestepValue,
  count: number,
  timestamp: bigint,
  limit: number,
  prefetchBehind: number,
  prefetchAhead: number,
  pointCloudTopicCount: number,
  store: RecordStore,
  notify: () => void,
): () => void {
  const recordsRequests = createRecordsRequests(
    pairedTopics,
    timestep,
    count,
    timestamp,
  );

  const minimumCacheCount = calculateMinimumCacheCount({
    count,
    prefetchBehind,
    prefetchAhead,
    limit,
  });
  const cacheCount = Math.max(
    Math.floor(MAX_IN_MEMORY_POINT_CLOUDS / pointCloudTopicCount),
    minimumCacheCount,
  );

  return mergeSubscriptions([
    ...recordsRequests.map((request, index) => {
      const { topic } = pairedTopics[index];

      return store.subscribe({
        ...request,
        limit,
        topicStartTime: topic.startTime,
        topicEndTime: topic.endTime,
        cacheCount,
        prefetchBehind,
        prefetchAhead,
        notify,
      });
    }),
    tfStaticTopic != null &&
      store.subscribe({
        type: "all",
        recordType: "default",
        topicId: tfStaticTopic.id,
        frequency: null,
        topicStartTime: tfStaticTopic.startTime,
        topicEndTime: tfStaticTopic.endTime,
        limit,
        notify,
      }),
  ]);
}

function getSnapshot(
  pairedTopics: PairedTopics<ThreeDPanel["topics"]>,
  tfStaticTopic: Topic | null,
  showRotationControls: boolean,
  timestep: TimestepValue,
  count: number,
  timestamp: bigint,
  store: RecordStore,
): AsyncOperationSnapshot<ThreeDRecords> {
  const recordsRequests = createRecordsRequests(
    pairedTopics,
    timestep,
    count,
    timestamp,
  );

  const responses = new Array<
    Extract<RecordsResponse<"threeD">, { status: "pending" | "fulfilled" }>
  >();
  for (let i = 0; i < recordsRequests.length; i++) {
    const response = store.getRecords(recordsRequests[i]);

    if (response.status === "rejected") {
      return response;
    }

    responses.push(response);
  }

  const tfStaticResponse =
    tfStaticTopic != null
      ? store.getRecords({
          type: "all",
          recordType: "default",
          topicId: tfStaticTopic.id,
          frequency: null,
        })
      : null;

  if (tfStaticResponse?.status === "rejected") {
    if (tfStaticResponse.reason instanceof RecordStoreError) {
      return tfStaticResponse;
    } else {
      return {
        status: "rejected",
        reason: new RecordStoreError({
          cause: tfStaticResponse.reason,
          topic: tfStaticTopic!,
        }),
      };
    }
  }

  if (!responses.every((response) => response.status === "fulfilled")) {
    return { status: "pending" };
  }

  if (tfStaticResponse?.status === "pending") {
    return tfStaticResponse;
  }

  const objects = new Array<ThreeDObject>();
  let firstTimestamp: bigint | null = null;
  for (let i = 0; i < responses.length; i++) {
    const { value: records } = responses[i];
    const { topic, descriptor } = pairedTopics[i];

    if (records.length === 0) {
      return {
        status: "rejected",
        reason: new NoRecordsError(),
      };
    }

    const mostRecentRecordIndex = records.findLastIndex(
      (record) => record.timestamp <= timestamp,
    );

    if (mostRecentRecordIndex >= 0) {
      objects.push({
        id: topic.id,
        type: "point-cloud",
        data: records[mostRecentRecordIndex].data,
        size: descriptor.size,
      });
    }

    if (firstTimestamp == null) {
      firstTimestamp = records[0].timestamp;
    } else {
      firstTimestamp = bigintMin(firstTimestamp, records[0].timestamp);
    }
  }

  let transforms = null;
  if (tfStaticResponse != null) {
    try {
      transforms = buildTransformTree(tfStaticResponse.value);
    } catch (e) {
      return {
        status: "rejected",
        reason: new RecordStoreError({
          cause: e,
          topic: tfStaticTopic!,
        }),
      };
    }
  }

  return {
    status: "fulfilled",
    value: {
      showRotationControls,
      objects,
      transforms,
      firstTimestamp: objects.length === 0 ? firstTimestamp : null,
    },
  };
}

export function useThreeDRecords({
  panel,
}: {
  panel: ThreeDPanel;
}): [threeDSnapshot: ThreeDStoreSnapshot, isPlaceholderSnapshot: boolean] {
  const { timestep } = usePlaybackSettings();
  const playbackSource = useLoadedPlaybackSource();

  const pointCloudTopicCount = usePointCloudTopicCount();

  const pairedTopics = usePairedTopics(panel);

  const { count, limit, prefetchBehind, prefetchAhead } =
    useVisualizationStoreParams(VisualizationType.ThreeD);

  const topics = useLoadedPlayerTopics();
  const tfStaticTopic = !panel.useStaticTransforms
    ? null
    : topics.find((topic) => topic.name === "/tf_static") ?? null;

  const storeSnapshotResult = useStoreSnapshot(
    useMemo(() => {
      const request: StoreRequest = {
        pairedTopics,
        tfStaticTopic,
        showRotationControls: panel.showRotationControls,
        timestep,
        count,
        timestamp: playbackSource.timestamp,
      };

      return {
        request,
        subscribe: subscribeToStore.bind(
          null,
          pairedTopics,
          tfStaticTopic,
          timestep,
          count,
          playbackSource.timestamp,
          limit,
          prefetchBehind,
          prefetchAhead,
          pointCloudTopicCount,
        ),
        getSnapshot: getSnapshot.bind(
          null,
          pairedTopics,
          tfStaticTopic,
          panel.showRotationControls,
          timestep,
          count,
          playbackSource.timestamp,
        ),
      };
    }, [
      pairedTopics,
      tfStaticTopic,
      panel.showRotationControls,
      timestep,
      count,
      playbackSource.timestamp,
      limit,
      prefetchBehind,
      prefetchAhead,
      pointCloudTopicCount,
    ]),
  );

  useSkipToFirstTimestamp(
    panel,
    pairedTopics[0].topic.startTime,
    storeSnapshotResult.snapshot.status === "fulfilled",
  );

  useUpdatePanelBuffering(
    storeSnapshotResult.snapshot.status === "pending" ||
      storeSnapshotResult.isPlaceholder,
  );

  return [storeSnapshotResult.snapshot, storeSnapshotResult.isPlaceholder];
}

/**
 * Determine the number of unique point cloud topics active in the Player right
 * now. Used to calculate the cache count to use for each unique topic. This
 * calculation only considers unique topics since several subscribers to the
 * same point cloud topic will all share references to the same underlying
 * buffers.
 */
function usePointCloudTopicCount(): number {
  const { panels } = usePanelLayoutContext();

  const uniquePointCloudTopics = new Set<Topic["name"]>();

  for (const panel of panels) {
    if (panel.visualization !== VisualizationType.ThreeD) {
      continue;
    }

    for (const descriptor of panel.topics) {
      // Assuming all 3D topics are point clouds.
      uniquePointCloudTopics.add(descriptor.name);
    }
  }

  return uniquePointCloudTopics.size;
}
